import { useEffect, useState } from "react";
import { styled } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faXmark } from "@fortawesome/pro-light-svg-icons";
import { transition } from "../styles/mixins";
import Window from "./common/Window";
import WaypointDetail from "./systems/WaypointDetail";
import FleetOverview from "./fleet/FleetOverview";
import System from "./systems/System";
import SystemDetail from "./systems/SystemDetail";
import ShipModal from "./fleet/ShipModal";
import ContractModal from "./contracts/ContractModal";
import MarketplaceModal from "./marketplace/MarketplaceModal";
import ShipyardModal from "./fleet/ShipyardModal";
import { TabItem, Tabs } from "./common/Tabs";
import Flow from "./common/Flow";
import ContractActive from "./contracts/ContractActive";
import Messages from "./common/Messages";
import FleetModal from "./fleet/FleetModal";
import SystemsModal from "./systems/SystemsModal";

const Root = styled.div`
  background-color: var(--color-theme-primary-600);
  display: flex;
  gap: var(--space-6);
  height: 100%;
  justify-content: space-between;
  max-height: calc(100vh - var(--space-10));
  overflow: hidden;
  padding: var(--space-6);
  position: relative;
  z-index: 1;

  &:has(.waypoint-open) {
    --zoom-indicator-right: calc(288px + 24px + 8px + 16px + 24px + var(--space-6));
  }
`;

const Fleet = styled(Window)`
  flex-basis: 72px;
  flex-grow: 0;
  flex-shrink: 1;
  position: relative;
  z-index: 1;
`;

const Waypoint = styled.div`
  ${transition('opacity, scale')}
  display: flex;
  flex: 1;
  height: 100%;
  justify-content: flex-end;
  opacity: ${props => props.$isOpen ? '1' : '0'};
  pointer-events: none;
  scale: ${props => props.$isOpen ? '1' : '0.75'};
  transform-origin: right bottom;
  position: relative;
  z-index: 1;
`;

const WaypointControls = styled(Window)`
  border-right-width: 0;
  display: flex;
  flex-basis: var(--space-6);
  flex-direction: column;
  flex-shrink: 0;
  pointer-events: all;
  position: relative;
  z-index: 1;
`;

const WaypointControl = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
`;

const WaypointClose = styled(WaypointControl)`
  aspect-ratio: 1;
  border-bottom: var(--border);
`;

const WaypointToggle = styled(WaypointControl)`
  flex: 1;
`;

const WaypointToggleIcon = styled(FontAwesomeIcon)`
  ${transition('rotate')}
  rotate: ${props => props.$resize ? '180deg' : '0deg'};
  transition-delay: 250ms;
`;

const WaypointWindow = styled(Window)`
  ${transition('flex-basis')}
  align-items: stretch;
  display: flex;
  flex-basis: ${props => props.$resize ? 'calc(50% + 24px)' : '288px'};
  pointer-events: ${props => props.$isOpen ? 'all' : 'none'};
  position: relative;
  z-index: 0;

  &::after {
    content: '';
    height: 100%;
    left: calc(var(--space-6) * -1);
    pointer-events: none;
    position: absolute;
    top: 0;
    width: calc(100% + var(--space-8));
    z-index: -1;
  }
`;

const SystemCurrent = styled.div`
  bottom: var(--space-6);
  max-width: 432px;
  left: calc(var(--space-6) + 72px + var(--space-6));
  position: absolute;
  width: 100%;
  background-color: var(--window-background);
  border: 1px solid var(--window-border);
  padding: var(--space-2);
`;

function Interface({ store }) {
  const [waypointResize, setWaypointResize] = useState(false);
  const [waypoint, setWaypoint] = useState(store.state.waypoint != undefined)
  const [tab, setTab] = useState('system');
  const modal = store.state.modal;

  const waypointResizeHandle = () => {
    if (waypointResize) {
      setWaypointResize(false);
    } else {
      setWaypointResize(true);
    }
  }

  const waypointCloseHandle = () => {
    setWaypoint(false);
    setWaypointResize(false);
  }

  useEffect(() => {
    if (store.state.waypoint != undefined) {
      setWaypoint(true);
    }
  }, [store.state.waypoint]);

  return (
    <Root>
      <System store={store} />
      <Fleet children={<FleetOverview store={store} />} />
      <SystemCurrent>
        <Flow $space={3}>
          <Tabs>
            <TabItem $active={tab == 'system'} onClick={() => setTab('system')}>System</TabItem>
            <TabItem $active={tab == 'contract'} onClick={() => setTab('contract')}>Contract</TabItem>
          </Tabs>
          {tab == 'system' && (
            <SystemDetail store={store} />
          )}
          {tab == 'contract' && (
            <ContractActive store={store} />
          )}
        </Flow>
      </SystemCurrent>
      <Waypoint $isOpen={waypoint} className={waypoint && 'waypoint-open'}>
        <WaypointControls>
          <WaypointClose onClick={waypointCloseHandle} children={<FontAwesomeIcon icon={faXmark} />} />
          <WaypointToggle onClick={waypointResizeHandle} children={<WaypointToggleIcon $resize={waypointResize} icon={faAngleLeft} />} />
        </WaypointControls>
        <WaypointWindow $padding $isOpen={waypoint} $resize={waypointResize} children={<WaypointDetail store={store} resize={waypointResize} />} />
      </Waypoint>
      {modal && modal.modal == 'ship' && <ShipModal store={store} />}
      {modal && modal.modal == 'marketplace' && <MarketplaceModal store={store} />}
      {modal && modal.modal == 'contracts' && <ContractModal store={store} />}
      {modal && modal.modal == 'shipyard' && <ShipyardModal store={store} />}
      {modal && modal.modal == 'fleet' && <FleetModal store={store} />}
      {modal && modal.modal == 'systems' && <SystemsModal store={store} />}
      <Messages store={store} />
    </Root>
  );
}

export default Interface;
